import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { of, Subscription } from "rxjs";
import { catchError, filter, timeout } from "rxjs/operators";
import { CartService } from "../../../cart/cart.service";
import { ShopCartService } from "../../../commerce/shop-cart.service";
import { EnvironmentConfig, isStorefrontEnabled } from "../../../core/core";

declare const environment: EnvironmentConfig;

const HOVER_OPEN_DELAY_MS = 120;
const HOVER_CLOSE_DELAY_MS = 220;
let nextPanelId = 0;

@Component({
  selector: "app-header-cart",
  templateUrl: "./header-cart.component.html",
  styleUrls: ["./header-cart.component.scss"],
})
export class HeaderCartComponent implements OnInit, OnDestroy {
  @Input() darkTheme: boolean = false;
  @Input() disableClick: boolean = false;
  @ViewChild("trigger") private trigger: ElementRef<HTMLButtonElement>;

  /** Without the storefront flag (Prod) the cart is rental-only and never requests the Shop cart. */
  readonly storefrontEnabled = isStorefrontEnabled(environment);
  readonly panelId = `header-cart-panel-${++nextPanelId}`;

  isOpen = false;
  /** Opened by click or keyboard: stays open when the pointer leaves. */
  private pinned = false;
  private hoverTimer: ReturnType<typeof setTimeout>;

  numberOfItemsInCart = 0;
  numberOfShopItems = 0;

  cartSubscription: Subscription;
  shopSubscription: Subscription;
  private loadSubscription: Subscription;
  private routeSubscription: Subscription;

  constructor(private cartService: CartService, private router: Router, private shopCart: ShopCartService, private host: ElementRef<HTMLElement>) {}

  ngOnInit() {
    // An inactive cart has been converted to an order, so its items no longer count.
    this.cartSubscription = this.cartService.cartBSubject.subscribe(cart => {
      const items = cart?.activeStatus === "Active" ? cart.items || [] : [];
      this.numberOfItemsInCart = items.length;
    });
    this.routeSubscription = this.router?.events?.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.close());
    if (!this.storefrontEnabled) return;

    this.shopSubscription = this.shopCart.cart$.subscribe(cart => {
      const lines = cart?.status === "Converted" ? [] : cart?.lines || [];
      this.numberOfShopItems = lines.reduce((sum, line) => sum + line.quantity, 0);
    });
    // The rental cart never waits on, or fails with, the Shop cart request.
    this.loadSubscription = this.shopCart.load().pipe(timeout(8000), catchError(() => of(null))).subscribe();
  }

  get badgeCount(): number {
    return this.numberOfItemsInCart + (this.storefrontEnabled ? this.numberOfShopItems : 0);
  }

  get triggerLabel(): string {
    const rentals = `${this.numberOfItemsInCart} rental ${this.numberOfItemsInCart === 1 ? "item" : "items"}`;
    return this.storefrontEnabled ? `Cart: ${rentals}, ${this.numberOfShopItems} Shop ${this.numberOfShopItems === 1 ? "item" : "items"}` : `Cart: ${rentals}`;
  }

  /** Click and keyboard pin the panel; a click on a hover-opened panel keeps it open instead of closing it. */
  toggle(): void {
    if (this.disableClick) return;
    clearTimeout(this.hoverTimer);
    if (this.isOpen && !this.pinned) { this.pinned = true; return; }
    if (this.isOpen) this.close(true);
    else this.show(true);
  }

  show(pin: boolean): void {
    if (this.disableClick) return;
    clearTimeout(this.hoverTimer);
    this.isOpen = true;
    this.pinned = this.pinned || pin;
    if (pin) setTimeout(() => (this.host.nativeElement.querySelector(`#${this.panelId}`) as HTMLElement)?.focus());
  }

  close(returnFocus = false): void {
    clearTimeout(this.hoverTimer);
    if (!this.isOpen) return;
    this.isOpen = false;
    this.pinned = false;
    if (returnFocus) this.trigger?.nativeElement.focus();
  }

  private get canHover(): boolean {
    return typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover) and (min-width: 992px)").matches;
  }

  @HostListener("mouseenter")
  pointerEntered(): void {
    if (!this.canHover || this.disableClick) return;
    clearTimeout(this.hoverTimer);
    if (!this.isOpen) this.hoverTimer = setTimeout(() => this.show(false), HOVER_OPEN_DELAY_MS);
  }

  @HostListener("mouseleave")
  pointerLeft(): void {
    if (!this.canHover) return;
    clearTimeout(this.hoverTimer);
    if (this.isOpen && !this.pinned) this.hoverTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY_MS);
  }

  @HostListener("document:click", ["$event"])
  documentClicked(event: MouseEvent): void {
    if (this.isOpen && !this.host.nativeElement.contains(event.target as Node)) this.close();
  }

  @HostListener("document:keydown.escape")
  escapePressed(): void {
    if (this.isOpen) this.close(true);
  }

  @HostListener("focusout", ["$event"])
  focusLeft(event: FocusEvent): void {
    const next = event.relatedTarget as Node;
    if (this.isOpen && next && !this.host.nativeElement.contains(next)) this.close();
  }

  ngOnDestroy() {
    clearTimeout(this.hoverTimer);
    this.cartSubscription?.unsubscribe();
    this.shopSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }
}
